import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Image, Calendar, MessageSquare, DollarSign, ArrowRight, Clock } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardHome() {
  const [stats, setStats] = useState({
    galleryCount: 0,
    inquiriesCount: 0,
    newInquiries: 0,
    pricingCount: 0,
  });
  const [recentInquiries, setRecentInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [galleryRes, inquiriesRes, pricingRes] = await Promise.all([
          axios.get(`${API}/gallery`),
          axios.get(`${API}/inquiries`),
          axios.get(`${API}/pricing`),
        ]);
        
        const inquiries = inquiriesRes.data;
        const newInquiries = inquiries.filter(i => i.status === 'new');
        
        setStats({
          galleryCount: galleryRes.data.length,
          inquiriesCount: inquiries.length,
          newInquiries: newInquiries.length,
          pricingCount: pricingRes.data.length,
        });
        
        setRecentInquiries(inquiries.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    { icon: Image, label: 'Gallery Images', value: stats.galleryCount, link: '/admin/gallery', color: 'bg-blue-50 text-blue-600' },
    { icon: MessageSquare, label: 'New Inquiries', value: stats.newInquiries, link: '/admin/inquiries', color: 'bg-green-50 text-green-600' },
    { icon: Calendar, label: 'Total Inquiries', value: stats.inquiriesCount, link: '/admin/inquiries', color: 'bg-purple-50 text-purple-600' },
    { icon: DollarSign, label: 'Pricing Seasons', value: stats.pricingCount, link: '/admin/pricing', color: 'bg-amber-50 text-amber-600' },
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-CA', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/Toronto'
      });
    }
    return new Date(dateStr).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'America/Toronto'
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-serif text-3xl text-[#1C1C1C] mb-2">Dashboard</h1>
        <p className="text-[#66605B]">Welcome to the Big Bear Estate admin panel.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.label}
              to={stat.link}
              className="bg-white p-6 rounded-sm shadow-sm hover:shadow-md transition-shadow"
            >
              <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center mb-4`}>
                <Icon className="w-6 h-6" />
              </div>
              <p className="text-3xl font-serif text-[#1C1C1C] mb-1">{stat.value}</p>
              <p className="text-sm text-[#66605B]">{stat.label}</p>
            </Link>
          );
        })}
      </div>

      {/* Recent Inquiries */}
      <div className="bg-white rounded-sm shadow-sm">
        <div className="p-6 border-b border-[#E2E0DC] flex items-center justify-between">
          <h2 className="font-serif text-xl text-[#1C1C1C]">Recent Inquiries</h2>
          <Link
            to="/admin/inquiries"
            className="text-sm text-[#1B4D3E] hover:text-[#D4AF37] flex items-center gap-1 transition-colors"
          >
            View All
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {recentInquiries.length > 0 ? (
          <div className="divide-y divide-[#E2E0DC]">
            {recentInquiries.map((inquiry) => (
              <div key={inquiry.id} className="p-6 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-[#1C1C1C] truncate">
                    {inquiry.first_name && inquiry.last_name 
                      ? `${inquiry.first_name} ${inquiry.last_name}` 
                      : inquiry.name}
                  </p>
                  <p className="text-sm text-[#66605B] truncate">{inquiry.email}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-[#66605B]">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {inquiry.check_in_date} → {inquiry.check_out_date}
                    </span>
                    <span>{inquiry.num_occupants || inquiry.guests} guests</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`
                    px-3 py-1 rounded-full text-xs font-medium
                    ${inquiry.status === 'new' ? 'bg-green-100 text-green-800' :
                      inquiry.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                      inquiry.status === 'confirmed' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'}
                  `}>
                    {inquiry.status}
                  </span>
                  <span className="text-xs text-[#66605B] flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(inquiry.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center text-[#66605B]">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p>No inquiries yet</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link
          to="/admin/gallery"
          className="bg-[#1B4D3E] text-white p-6 rounded-sm hover:bg-[#153E32] transition-colors"
        >
          <Image className="w-8 h-8 mb-4" />
          <h3 className="font-serif text-lg mb-1">Manage Gallery</h3>
          <p className="text-white/70 text-sm">Add, edit, or remove property photos</p>
        </Link>
        <Link
          to="/admin/bookings"
          className="bg-[#1B4D3E] text-white p-6 rounded-sm hover:bg-[#153E32] transition-colors"
        >
          <Calendar className="w-8 h-8 mb-4" />
          <h3 className="font-serif text-lg mb-1">Update Availability</h3>
          <p className="text-white/70 text-sm">Mark dates as booked or available</p>
        </Link>
        <Link
          to="/admin/content"
          className="bg-[#1B4D3E] text-white p-6 rounded-sm hover:bg-[#153E32] transition-colors"
        >
          <DollarSign className="w-8 h-8 mb-4" />
          <h3 className="font-serif text-lg mb-1">Edit Content</h3>
          <p className="text-white/70 text-sm">Update property details and information</p>
        </Link>
      </div>
    </div>
  );
}
